/**
 * Geo helpers for the "Closest to you" feed strip.
 *
 * Coordinates are plain { lat, lng } in decimal degrees. Distances are computed
 * client-side with the haversine formula — accurate to well within a few metres
 * at city scale, and far cheaper than pulling in a geo library.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_KM = 6371;
const toRad = (deg: number) => (deg * Math.PI) / 180;

/** Great-circle distance between two points, in kilometres. */
export function haversineKm(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** A finite { lat, lng } from a possibly-null restaurant row, or null. */
export function coordsOf(r: { lat?: number | null; lng?: number | null }): LatLng | null {
  if (typeof r.lat === "number" && typeof r.lng === "number" && Number.isFinite(r.lat) && Number.isFinite(r.lng)) {
    return { lat: r.lat, lng: r.lng };
  }
  return null;
}

/**
 * Human-readable distance. Under 1 km → metres rounded to the nearest 10
 * ("250 m"); 1–10 km → one decimal ("1.2 km"); above → whole km ("12 km").
 * `lang` only switches the decimal separator (RO uses a comma).
 */
export function formatDistance(km: number, lang: "en" | "ro" = "en"): string {
  if (km < 1) {
    const m = Math.max(10, Math.round((km * 1000) / 10) * 10);
    return `${m} m`;
  }
  const value = km < 10 ? km.toFixed(1) : String(Math.round(km));
  return `${lang === "ro" ? value.replace(".", ",") : value} km`;
}

/** Ignore jitter: a fix republishes once the user has moved about this far. */
const MIN_MOVE_KM = 0.025;

/**
 * Should a new GPS fix replace the published one? The first fix, a real move
 * (≥ 25 m), or a markedly sharper fix at the same spot (a coarse wifi/IP first
 * fix must not block the 50 m accuracy that a bonus stamp needs until the
 * diner walks 25 m). Small accuracy wobbles never republish.
 */
export function shouldPublishFix(
  prev: LatLng | null,
  prevAccuracyM: number | null,
  next: LatLng,
  nextAccuracyM: number | null,
): boolean {
  if (!prev) return true;
  if (haversineKm(prev, next) >= MIN_MOVE_KM) return true;
  if (nextAccuracyM == null) return false;
  if (prevAccuracyM == null) return true;
  return nextAccuracyM <= prevAccuracyM * 0.6 && prevAccuracyM - nextAccuracyM >= 10;
}

/**
 * The check-in radius: Dine In shows within it, and the server refuses
 * anything farther (record_visit §31c, start_redemption §33c).
 */
export const PROXIMITY_M = 75;
